function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { li as LiMotion } from 'motion/react-client';
import { BottomSheet, BottomSheetContentLayout, Box, Button, Callout, Column, ConfirmationModal, POPOVER_ARROW_ICON_WIDTH, PopoverArrowIcon, Row, TextButton, Typography } from 'seagull';
import { AddCircleLiner24, BankLine, Bulb24, CardLine, Plus } from 'seagull/icons';
import { useResponsive } from "../../client-flow/contexts";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { useDisconnectPaymentMethod } from "../../hooks/useDisconnectPaymentMethod";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useRemovePaymentMethod } from "../../hooks/useRemovePaymentMethod";
import { Intent } from "../../infra/access-control";
import { handlePromise } from "../../infra/utils";
import { paymentMethodModel } from "../../models";
import { PaymentMethodTypes, PlaidBankAccountStatusTypes } from "../../models/payment-method";
import { RelationshipProgressStatus } from "../../models/relationship";
import { BusinessChannelEventBinder, PaymentMethodEventType, PusherEvent } from "../../pusher";
import { selectors } from "../../store/state-selectors";
import LoadingIndicator from "../LoadingIndicator";
import RestfulWrapper from "../RestfulWrapper/RestfulWrapper";
import BankAccountPanel from "./BankAccountPanel";
import ConnectCreditCard from "./ConnectCreditCard";
import ConnectPlaid from "./ConnectPlaid";
import CreditCardPanel from "./CreditCardPanel";
import { getReadonlyWarning } from "./getReadonlyWarning";
import styles from "./ManagePaymentMethods.module.scss";
import { doPaymentMethodAction as _doPaymentMethodAction, fetchPaymentMethods, getRequiresPMActionConfirmation, isSelectablePaymentMethod } from "./ManagePaymentMethods.utils";
import PaymentMethodOption from "./PaymentMethodOption";
import { PaymentMethodPanelSkeleton } from "./PaymentMethodPanel";
import { useInviteMemberModal } from "./useInviteMemberModal";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const addAnotherBtnText = 'Add another payment method';
var SelectPaymentMethodOption = /*#__PURE__*/function (SelectPaymentMethodOption) {
  SelectPaymentMethodOption["None"] = "none";
  SelectPaymentMethodOption["Plaid"] = "plaid";
  SelectPaymentMethodOption["CreditCard"] = "creditCard";
  return SelectPaymentMethodOption;
}(SelectPaymentMethodOption || {});
export function ActualManagePaymentMethods(props) {
  const {
    vendorId,
    customerId,
    relationshipId,
    isPreview,
    isMemberClient,
    isSample,
    showCreditCardFee,
    selectedFundingSourceId,
    isVendorCoveringFees,
    isProposal = false,
    isRefreshing,
    isTerminatedAgreement = false,
    isInviteMemberDisabled,
    hideInviteMemberHint = false,
    isAddingAnotherPaymentMethod,
    hideExpandableAddPmButton,
    onSelectPaymentMethod,
    onPaymentMethodUpdated,
    setIsAddingAnotherPaymentMethod
  } = props;
  const {
    PAYMENT_METHODS_CHANGE_ANIMATION,
    CLIENT_ONBOARDING_NEW_FLOW
  } = useFeatureFlags();
  const {
    isMobile
  } = useResponsive();
  const {
    apiGatewayHost
  } = useStore(state => state.session);
  const isVendor = useStore(selectors.user.isVendor);
  const userRole = useStore(selectors.user.role);
  const canVendorControlPM = isVendor && !isProposal;
  const requiresPMActionConfirmation = getRequiresPMActionConfirmation(isVendor, userRole, isProposal);
  const {
    removePaymentMethodWithConfirmation,
    removePaymentMethod
  } = useRemovePaymentMethod();
  const disconnectPaymentMethod = useDisconnectPaymentMethod();
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const [arrowLeft, setArrowLeft] = useState(0);
  const [achButtonEl, setAchButtonEl] = useState(null);
  const [ccButtonEl, setCcButtonEl] = useState(null);
  const [selectedOption, setSelectedOption] = useState(SelectPaymentMethodOption.None);
  const [isAddAnotherSheetOpen, setIsAddAnotherSheetOpen] = useState(false);
  const [addAnotherSheetStep, setAddAnotherSheetStep] = useState('selection');
  const [isPaymentMethodInMotion, setIsPaymentMethodInMotion] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState(props.paymentMethods);
  useEffect(() => setPaymentMethods(props.paymentMethods), [props.paymentMethods]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(() => {
    if (isProposal) {
      var _paymentMethods$;
      return selectedFundingSourceId || ((_paymentMethods$ = paymentMethods[0]) === null || _paymentMethods$ === void 0 ? void 0 : _paymentMethods$.id) || null;
    } else {
      return selectedFundingSourceId || null;
    }
  });
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(paymentMethods.length === 0);
  const [fundingSourceIdsAdded, setFundingSourceIdsAdded] = useState([]);
  const selectedPM = paymentMethods.find(pm => pm.id === selectedPaymentMethod);
  const selectedPMRequiresVerification = !!selectedPM && paymentMethodModel.requiresVerification(selectedPM);
  useEffect(() => {
    if (selectedPaymentMethod && isProposal) {
      onSelectPaymentMethod && onSelectPaymentMethod(selectedPM);
    } else {
      onSelectPaymentMethod && onSelectPaymentMethod();
    }
  }, [selectedPaymentMethod]);
  useEffect(() => {
    if (isAddingAnotherPaymentMethod) {
      setIsAddingPaymentMethod(isAddingAnotherPaymentMethod);
    }
  }, [isAddingAnotherPaymentMethod]);
  useEffect(() => {
    const applyNewArrowLeft = buttonEl => {
      const {
        offsetLeft,
        clientWidth
      } = buttonEl;
      const arrowLeft = offsetLeft + clientWidth / 2 - POPOVER_ARROW_ICON_WIDTH / 2;
      setArrowLeft(arrowLeft);
    };
    if (selectedOption === SelectPaymentMethodOption.Plaid && achButtonEl) {
      applyNewArrowLeft(achButtonEl);
    } else if (selectedOption === SelectPaymentMethodOption.CreditCard && ccButtonEl) {
      applyNewArrowLeft(ccButtonEl);
    }
  }, [selectedOption, achButtonEl, ccButtonEl]);
  useEffect(() => {
    if (paymentMethods.length === 0) {
      // This will happen after removing the only payment-method.
      setIsAddingPaymentMethod(true);
    }
  }, [paymentMethods]);
  const disabledReason = getReadonlyWarning({
    isPreview,
    isMemberClient,
    isSampleRelationship: isSample
  });
  const sortedPaymentMethods = useMemo(() => {
    return paymentMethods.slice().sort((pmA, pmB) => {
      if (!selectedPaymentMethod || !PAYMENT_METHODS_CHANGE_ANIMATION && isProposal) return 0;
      if (pmB.id === selectedPaymentMethod) return 1;
      if (pmA.id === selectedPaymentMethod) return -1;
      return 0;
    });
  }, [paymentMethods, selectedPaymentMethod, isProposal]);
  async function refresh() {
    const result = await props.refresh();
    setPaymentMethods(result.paymentMethods);
    return result;
  }
  async function onPlaidSuccess(account, paymentMethodAction) {
    const fundingSourceId = account.id;
    if (isProposal) {
      api.relationship.updateProgressStatus(relationshipId, RelationshipProgressStatus.PaymentMethod);
    }
    logBI(10807);
    if (paymentMethodAction.type === 'replace') {
      const pmToReplace = paymentMethods.find(pm => pm.id === paymentMethodAction.fundingSourceId);
      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodAction.fundingSourceId));
      removePaymentMethod({
        fundingSourceId: paymentMethodAction.fundingSourceId,
        vendorId,
        customerId
      }).then(result => {
        if (!result.success && pmToReplace) {
          setPaymentMethods(prev => [...prev, pmToReplace]);
          overlayContext.showErrorToast('Failed to remove the existing payment method. Please remove it using the 3 dots menu', 'failed-remove-pm-toast');
        }
      });
    }
    setIsAddingPaymentMethod(false);
    setIsAddingAnotherPaymentMethod && setIsAddingAnotherPaymentMethod(false);
    if (isAddAnotherSheetOpen) closeAddAnotherSheet();
    setFundingSourceIdsAdded(fundingSourceIdsAdded.concat(fundingSourceId));
    // The rest of the flow is done after receiving a paymentMethodEvent in the subscriber callback below.
  }
  const connectPaymentMethodModal = async paymentMethod => {
    await overlayContext.showModal({
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "Connect payment method",
          testId: "connect-payment-method-modal",
          content: /*#__PURE__*/_jsx(Typography, {
            children: "Would you like to set this payment method as the default payment method for this agreement?"
          }),
          onPrimaryButtonClick: () => {
            dismissModal(true);
            _onSelect(paymentMethod);
          },
          onSecondaryButtonClick: () => {
            dismissModal(false);
          },
          primaryButtonLabel: "Yes",
          secondaryButtonLabel: "No"
        });
      }
    });
  };
  async function onPlaidVerify(fundingSourceId) {
    const {
      paymentMethods
    } = await refresh();
    const pm = paymentMethods.find(paymentMethod => paymentMethod.id === fundingSourceId);
    if (pm) {
      if (shouldSelectAfterAddingPM(paymentMethods)) {
        _onSelect(pm);
      } else {
        await connectPaymentMethodModal(pm);
      }
    }
  }
  async function onConnectCreditCard(fundingSourceId) {
    setIsAddingPaymentMethod(false);
    setIsAddingAnotherPaymentMethod && setIsAddingAnotherPaymentMethod(false);
    if (isAddAnotherSheetOpen) closeAddAnotherSheet();
    const {
      paymentMethods
    } = await refresh();
    const pm = paymentMethods.find(paymentMethod => paymentMethod.id === fundingSourceId);
    if (pm) {
      if (shouldSelectAfterAddingPM(paymentMethods)) {
        _onSelect(pm);
      } else {
        await connectPaymentMethodModal(pm);
      }
    }
  }
  async function _onSelect(paymentMethod) {
    if (isProposal) {
      setSelectedPaymentMethod(paymentMethod.id);
    } else {
      overlayContext.startLoader();
      const {
        success
      } = await handlePromise(api.relationship.changePaymentMethod(relationshipId, paymentMethod.id));
      if (!success) {
        overlayContext.showErrorToast('Failed to connect payment method');
      } else {
        await refresh();
        setSelectedPaymentMethod(paymentMethod.id);
      }
      overlayContext.stopLoader();
    }
    setIsPaymentMethodInMotion(true);
  }
  function openOnBehalfModal(onConfirmation, paymentMethod) {
    const action = paymentMethod ? 'set' : 'add';
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "".concat(paymentMethod ? 'Setting' : 'Adding', " a payment method on behalf of your client"),
          textAlignCenter: false,
          content: /*#__PURE__*/_jsx(_Fragment, {
            children: /*#__PURE__*/_jsxs(Typography, {
              children: ["By proceeding I confirm that I am authorized by my clients to ", action, " a payment method and initiate billing under the applicable agreement. Anchor is not a party to this agreement, and all responsibility for this authorization and related actions lies with the undersigned."]
            })
          }),
          onPrimaryButtonClick: async () => {
            onConfirmation();
            dismissModal(true);
          },
          onSecondaryButtonClick: () => dismissModal(false),
          primaryButtonLabel: "Proceed",
          secondaryButtonLabel: "Back",
          primaryButtonTestId: "proceed-to-".concat(action, "-PM"),
          secondaryButtonTestId: "cancel-".concat(action, "-PM"),
          testId: "".concat(action, "-on-behalf-modal")
        });
      }
    });
  }
  function addAnotherPaymentMethod() {
    if (CLIENT_ONBOARDING_NEW_FLOW && isMobile && paymentMethods.length > 0) {
      setAddAnotherSheetStep('selection');
      setIsAddAnotherSheetOpen(true);
    } else {
      setSelectedOption(isMobile ? SelectPaymentMethodOption.None : SelectPaymentMethodOption.CreditCard);
      setIsAddingPaymentMethod(true);
    }
  }
  function closeAddAnotherSheet() {
    setIsAddAnotherSheetOpen(false);
    setAddAnotherSheetStep('selection');
  }
  async function onPaymentMethodPushEvent(_ref3) {
    let {
      payload
    } = _ref3;
    const paymentMethod = payload.paymentMethod;
    if (payload.type === PaymentMethodEventType.PaymentMethodUpdated && onPaymentMethodUpdated) {
      onPaymentMethodUpdated();
    }
    const {
      paymentMethods: newPaymentMethods
    } = await refresh();
    if (payload.type === PaymentMethodEventType.FailedToAddPaymentMethod) {
      if (!paymentMethod) return;
      if (fundingSourceIdsAdded.some(id => id === paymentMethod.fundingSourceId)) {
        setFundingSourceIdsAdded(fundingSourceIdsAdded.filter(id => id !== paymentMethod.fundingSourceId));
        overlayContext.showErrorToast('The bank account you tried to add is already connected', 'add-duplicate-pm-toast');
      } else if (paymentMethods.some(pm => pm.id === paymentMethod.fundingSourceId && paymentMethodModel.isPendingManualVerification(pm))) {
        overlayContext.showErrorToast('The bank account you tried to verify is already connected', 'verify-duplicate-pm-toast');
      }
    } else if (payload.type === PaymentMethodEventType.PaymentMethodAdded && fundingSourceIdsAdded.length) {
      const addedPM = newPaymentMethods.find(pm => fundingSourceIdsAdded.includes(pm.id));
      if (addedPM) {
        if (shouldSelectAfterAddingPM(newPaymentMethods)) {
          _onSelect(addedPM);
        } else {
          if (addedPM.type === PaymentMethodTypes.BankAccount && addedPM.bankAccount.verificationStatus !== PlaidBankAccountStatusTypes.PendingManualVerification) {
            await connectPaymentMethodModal(addedPM);
          }
        }
        setFundingSourceIdsAdded(fundingSourceIdsAdded.filter(id => addedPM.id !== id));
      }
    }
  }
  function shouldSelectAfterAddingPM(newPaymentMethods) {
    return newPaymentMethods.length === 1 || isProposal || selectedPMRequiresVerification;
  }
  const connectPaymentMethodProps = {
    relationshipId,
    vendorId,
    customerId: customerId,
    disabledReason,
    updateProgress: isProposal,
    doPaymentMethodAction: (action, paymentMethod) => _doPaymentMethodAction(action, requiresPMActionConfirmation, openOnBehalfModal, paymentMethod)
  };
  const showConnectAch = isMobile || selectedOption === SelectPaymentMethodOption.Plaid || addAnotherSheetStep === 'ach';
  const connectAch = showConnectAch && /*#__PURE__*/_jsx(ConnectPlaid, _objectSpread(_objectSpread({}, connectPaymentMethodProps), {}, {
    onSuccess: onPlaidSuccess
  }));
  const showConnectCreditCard = isMobile || selectedOption === SelectPaymentMethodOption.CreditCard || selectedOption === SelectPaymentMethodOption.None && !isMobile || addAnotherSheetStep === 'card';
  const connectCreditCard = showConnectCreditCard && /*#__PURE__*/_jsx(ConnectCreditCard, _objectSpread(_objectSpread({}, connectPaymentMethodProps), {}, {
    onConnect: onConnectCreditCard
  }));
  const {
    openInviteMemberModal
  } = useInviteMemberModal();
  const inviteHintTop = !hideInviteMemberHint && /*#__PURE__*/_jsxs(Row, {
    gap: 12,
    alignItems: "center",
    className: styles.inviteMemberHintPrimary,
    testId: "invite-member-hint-top",
    children: [/*#__PURE__*/_jsx(Bulb24, {
      className: styles.inviteMemberHintIcon
    }), /*#__PURE__*/_jsxs(Typography, {
      variant: "paragraph1",
      as: "span",
      children: ["Need help adding a payment method?", ' ', /*#__PURE__*/_jsx(TextButton, {
        skin: "primary",
        underline: "none",
        weight: "normal",
        intent: Intent.InviteTeamMember,
        disabled: isInviteMemberDisabled,
        onClick: openInviteMemberModal,
        testId: "invite-member",
        children: "Invite a team member"
      })]
    })]
  });
  const inviteCallout = !hideInviteMemberHint && /*#__PURE__*/_jsx(Box, {
    className: cn(styles.inviteMemberCallout, {
      [styles.isMobile]: isMobile
    }),
    style: {
      maxWidth: isMobile ? '100%' : 560
    },
    children: /*#__PURE__*/_jsxs(Callout, {
      intent: "info",
      testId: "invite-member-callout",
      children: ["Need help adding a payment method?", ' ', /*#__PURE__*/_jsx(TextButton, {
        intent: Intent.InviteTeamMember,
        disabled: isInviteMemberDisabled,
        onClick: openInviteMemberModal,
        testId: "invite-member",
        children: "Invite a team member"
      })]
    })
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [CLIENT_ONBOARDING_NEW_FLOW && inviteHintTop, /*#__PURE__*/_jsx(BusinessChannelEventBinder, {
      source: "mgmt",
      eventName: PusherEvent.PaymentMethod,
      callback: onPaymentMethodPushEvent
    }), (!isRefreshing || sortedPaymentMethods.length > 0 || fundingSourceIdsAdded.length > 0) && (sortedPaymentMethods.length > 0 || fundingSourceIdsAdded.length > 0) && /*#__PURE__*/_jsxs(Box, {
      as: "ul",
      className: cn(styles.existingPaymentMethods, hideExpandableAddPmButton && styles.noAddPaymentTextButton),
      children: [sortedPaymentMethods.map(paymentMethod => {
        const isSelected = paymentMethod.id === selectedPaymentMethod;
        const disconnectable = canVendorControlPM && isSelected;
        const commonProps = {
          key: paymentMethod.id,
          isProposal,
          isSelected,
          isPaymentMethodInMotion,
          apiGatewayHost,
          removable: !isTerminatedAgreement && (!isSelected || paymentMethodModel.failedVerification(paymentMethod)) && !isProposal && paymentMethod.isRemovable && !isMemberClient,
          selectable: isSelectablePaymentMethod(paymentMethod, isTerminatedAgreement, isSelected, isProposal, selectedPMRequiresVerification),
          disconnectable,
          verifyable: !isTerminatedAgreement,
          hasPermissionToVerify: !isMemberClient,
          onRemove: () => removePaymentMethodWithConfirmation({
            fundingSourceId: paymentMethod.id,
            vendorId,
            customerId: props.customerId,
            isCreditCard: paymentMethodModel.isCreditCard(paymentMethod),
            last4: paymentMethod.last4,
            async onSuccess() {
              await refresh();
              onPaymentMethodUpdated && onPaymentMethodUpdated();
            }
          }),
          onDisconnect: () => disconnectPaymentMethod({
            relationshipId,
            isCreditCard: paymentMethodModel.isCreditCard(paymentMethod),
            last4: paymentMethod.last4,
            async onSuccess() {
              setSelectedPaymentMethod(null);
            }
          }),
          onSelect: () => _doPaymentMethodAction(() => _onSelect(paymentMethod), requiresPMActionConfirmation, openOnBehalfModal, paymentMethod)
        };
        if (PAYMENT_METHODS_CHANGE_ANIMATION) {
          return /*#__PURE__*/_jsx(LiMotion, {
            layout: "position",
            transition: {
              type: 'tween',
              duration: 0.3
            },
            style: {
              zIndex: (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.id) === selectedPaymentMethod ? 1 : 0
            },
            onLayoutAnimationComplete: () => {
              setIsPaymentMethodInMotion(false);
            },
            children: paymentMethodModel.isBankAccount(paymentMethod) ? /*#__PURE__*/_jsx(BankAccountPanel, _objectSpread(_objectSpread({}, commonProps), {}, {
              bankAccount: paymentMethod,
              relationshipId: relationshipId,
              customerId: customerId,
              vendorId: vendorId,
              onSuccess: onPlaidSuccess,
              onVerify: onPlaidVerify
            })) : /*#__PURE__*/_jsx(CreditCardPanel, _objectSpread(_objectSpread({}, commonProps), {}, {
              creditCard: paymentMethod
            }))
          }, paymentMethod.id);
        }
        return paymentMethodModel.isBankAccount(paymentMethod) ? /*#__PURE__*/_jsx(BankAccountPanel, _objectSpread(_objectSpread({}, commonProps), {}, {
          bankAccount: paymentMethod,
          relationshipId: relationshipId,
          customerId: customerId,
          vendorId: vendorId,
          onSuccess: onPlaidSuccess,
          onVerify: onPlaidVerify
        })) : /*#__PURE__*/_jsx(CreditCardPanel, _objectSpread(_objectSpread({}, commonProps), {}, {
          creditCard: paymentMethod
        }));
      }), fundingSourceIdsAdded.map((_, idx) => /*#__PURE__*/_jsx(PaymentMethodPanelSkeleton, {}, idx))]
    }), !isTerminatedAgreement && paymentMethods.length > 0 && !hideExpandableAddPmButton && (isAddingPaymentMethod ? CLIENT_ONBOARDING_NEW_FLOW ? /*#__PURE__*/_jsx(Typography, {
      className: styles.addPaymentMethod,
      variant: "paragraph2",
      weight: "normal",
      skin: "secondary",
      children: addAnotherBtnText
    }) : /*#__PURE__*/_jsx(Typography, {
      className: cn(styles.addPaymentMethod, styles.addPaymentMethodLegacy),
      skin: "secondary",
      children: addAnotherBtnText
    }) : CLIENT_ONBOARDING_NEW_FLOW ? /*#__PURE__*/_jsx(Row, {
      children: /*#__PURE__*/_jsx(TextButton, {
        className: styles.addPaymentMethod,
        variant: "paragraph2",
        weight: "normal",
        underline: "none",
        prefixIcon: /*#__PURE__*/_jsx(AddCircleLiner24, {
          size: 18
        }),
        testId: "manage-payment-methods-add-payment-method",
        intent: Intent.AddPaymentMethod,
        onClick: addAnotherPaymentMethod,
        children: addAnotherBtnText
      })
    }) : /*#__PURE__*/_jsx(TextButton, {
      className: cn(styles.addPaymentMethod, styles.addPaymentMethodLegacy),
      prefixIcon: /*#__PURE__*/_jsx(Plus, {}),
      testId: "manage-payment-methods-add-payment-method",
      intent: Intent.AddPaymentMethod,
      onClick: addAnotherPaymentMethod,
      children: addAnotherBtnText
    })), !isTerminatedAgreement && isAddingPaymentMethod && (!isMobile || paymentMethods.length === 0 || !!hideExpandableAddPmButton) && /*#__PURE__*/_jsxs(Box, {
      className: cn(styles.options, {
        [styles.isMobile]: isMobile,
        [styles.ccNoFee]: !showCreditCardFee,
        [styles.optionsNewUi]: CLIENT_ONBOARDING_NEW_FLOW
      }),
      style: !isMobile && !CLIENT_ONBOARDING_NEW_FLOW ? {
        maxWidth: 600
      } : undefined,
      children: [/*#__PURE__*/_jsx(PaymentMethodOption, {
        className: styles.optionsOption,
        text: CLIENT_ONBOARDING_NEW_FLOW ? 'Card' : 'Credit card',
        parenthesisText: canVendorControlPM ? "fee paid by ".concat(isVendorCoveringFees ? 'you' : 'client') : showCreditCardFee ? '2.9%+30¢ fee' : 'No fee',
        icon: /*#__PURE__*/_jsx(CardLine, {}),
        selected: selectedOption === SelectPaymentMethodOption.CreditCard,
        testId: "manage-payment-methods-credit-card",
        ref: setCcButtonEl,
        onOptionClick: () => setSelectedOption(SelectPaymentMethodOption.CreditCard),
        mobileView: isMobile,
        sheetTitle: isMobile ? 'Card' : undefined,
        onSheetClose: isMobile ? () => setSelectedOption(SelectPaymentMethodOption.None) : undefined,
        children: connectCreditCard
      }), /*#__PURE__*/_jsx(PaymentMethodOption, {
        className: styles.optionsOption,
        text: "Secure ACH",
        parenthesisText: canVendorControlPM ? '' : 'No fee',
        icon: /*#__PURE__*/_jsx(BankLine, {}),
        selected: selectedOption === SelectPaymentMethodOption.Plaid,
        testId: "manage-payment-methods-secure-ach",
        ref: setAchButtonEl,
        onOptionClick: () => setSelectedOption(SelectPaymentMethodOption.Plaid),
        mobileView: isMobile,
        sheetTitle: isMobile ? 'Secure ACH' : undefined,
        onSheetClose: isMobile ? () => setSelectedOption(SelectPaymentMethodOption.None) : undefined,
        children: connectAch
      }), !isMobile && /*#__PURE__*/_jsxs(Box, {
        className: cn(styles.box, {
          [styles.boxNewUi]: CLIENT_ONBOARDING_NEW_FLOW
        }),
        children: [!CLIENT_ONBOARDING_NEW_FLOW && /*#__PURE__*/_jsx(PopoverArrowIcon, {
          className: styles.arrow,
          style: {
            left: arrowLeft
          }
        }), compact([connectAch, connectCreditCard])]
      })]
    }), CLIENT_ONBOARDING_NEW_FLOW && isMobile && paymentMethods.length > 0 && !hideExpandableAddPmButton && /*#__PURE__*/_jsxs(BottomSheet, {
      open: isAddAnotherSheetOpen,
      onClose: closeAddAnotherSheet,
      showBackButton: addAnotherSheetStep !== 'selection',
      onBack: () => setAddAnotherSheetStep('selection'),
      testId: "add-another-payment-method-sheet",
      children: [addAnotherSheetStep === 'selection' && /*#__PURE__*/_jsx(BottomSheetContentLayout, {
        header: {
          title: 'Add a payment method',
          paddingBottom: 16
        },
        contentGutter: "m",
        children: /*#__PURE__*/_jsxs(Column, {
          gap: 16,
          children: [/*#__PURE__*/_jsx(Button, {
            type: "button",
            className: styles.addAnotherSheetOption,
            onClick: () => setAddAnotherSheetStep('card'),
            testId: "add-another-sheet-card-option",
            children: /*#__PURE__*/_jsxs(Row, {
              gap: 8,
              alignItems: "center",
              className: styles.addAnotherSheetOptionRow,
              children: [/*#__PURE__*/_jsx(Box, {
                className: styles.addAnotherSheetOptionIcon,
                children: /*#__PURE__*/_jsx(CardLine, {})
              }), /*#__PURE__*/_jsx(Box, {
                className: styles.addAnotherSheetOptionName,
                children: /*#__PURE__*/_jsx(Typography, {
                  as: "span",
                  variant: "paragraph1",
                  skin: "standard",
                  weight: "normal",
                  children: "Card"
                })
              }), /*#__PURE__*/_jsx(Typography, {
                as: "span",
                variant: "paragraph1",
                skin: "secondary",
                className: styles.addAnotherSheetOptionFee,
                children: showCreditCardFee ? '2.9%+30¢ fee' : 'No fee'
              })]
            })
          }), /*#__PURE__*/_jsx(Button, {
            type: "button",
            className: styles.addAnotherSheetOption,
            onClick: () => setAddAnotherSheetStep('ach'),
            testId: "add-another-sheet-ach-option",
            children: /*#__PURE__*/_jsxs(Row, {
              gap: 8,
              alignItems: "center",
              className: styles.addAnotherSheetOptionRow,
              children: [/*#__PURE__*/_jsx(Box, {
                className: styles.addAnotherSheetOptionIcon,
                children: /*#__PURE__*/_jsx(BankLine, {})
              }), /*#__PURE__*/_jsx(Box, {
                className: styles.addAnotherSheetOptionName,
                children: /*#__PURE__*/_jsx(Typography, {
                  as: "span",
                  variant: "paragraph1",
                  skin: "standard",
                  weight: "normal",
                  children: "Secure ACH"
                })
              }), /*#__PURE__*/_jsx(Typography, {
                as: "span",
                variant: "paragraph1",
                skin: "secondary",
                className: styles.addAnotherSheetOptionFee,
                children: "No fee"
              })]
            })
          })]
        })
      }), addAnotherSheetStep === 'card' && /*#__PURE__*/_jsx(BottomSheetContentLayout, {
        header: {
          title: 'Card',
          paddingBottom: 16
        },
        contentGutter: "m",
        children: connectCreditCard
      }), addAnotherSheetStep === 'ach' && /*#__PURE__*/_jsx(BottomSheetContentLayout, {
        header: {
          title: 'Secure ACH',
          paddingBottom: 16
        },
        contentGutter: "m",
        contentClassName: styles.achSheetContentFullHeight,
        children: connectAch
      })]
    }), !CLIENT_ONBOARDING_NEW_FLOW && sortedPaymentMethods.length === 0 && inviteCallout]
  });
}
export default function ManagePaymentMethods(props) {
  const {
    isPreview,
    isSample,
    relationshipId,
    isProposal,
    vendorId,
    customerId,
    initialSelectedPaymentMethodId
  } = props;
  const {
    api
  } = useStoreActions();
  const businessId = useStore(selectors.user.businessId);
  const fetch = () => {
    return fetchPaymentMethods({
      isSample,
      vendorId,
      isPreview,
      businessId,
      isProposal,
      initialSelectedPaymentMethodId,
      clientGetPaymentMethod: () => api.paymentMethod.clientGet(vendorId),
      vendorGetPaymentMethod: () => api.paymentMethod.vendorGet(customerId),
      getSelectedPaymentMethod: () => api.relationship.getSelectedPaymentMethod(relationshipId)
    });
  };
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: fetch,
    renderChild: _ref4 => {
      let {
        data,
        loading,
        refresh
      } = _ref4;
      return /*#__PURE__*/_jsx(ActualManagePaymentMethods, _objectSpread(_objectSpread({}, props), {}, {
        paymentMethods: data.paymentMethods,
        selectedFundingSourceId: data.selectedFundingSourceId,
        isRefreshing: loading,
        refresh: refresh
      }));
    },
    renderLoader: () => /*#__PURE__*/_jsx(LoadingIndicator, {
      testId: "manage-payment-methods-loading-indicator"
    })
  });
}